import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '@/services/api';
import Button from '@/components/ui/Button';
import Skeleton from '@/components/ui/Skeleton';
import { BadgeCheck, AlertTriangle, CreditCard } from 'lucide-react';

/**
 * Subscription — how the practice pays Cervica (Stripe). Owner-only tab.
 * Distinct from every other billing surface, which is patient/insurance
 * money. Checkout and card management happen on Stripe-hosted pages so
 * card data never touches Cervica.
 */
export default function SubscriptionTab() {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['billing-account'],
    queryFn: () => api.get('/billing-account').then((r) => r.data),
  });
  const [busyPlan, setBusyPlan] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [locations, setLocations] = useState(2);
  const [confirmation, setConfirmation] = useState('');
  const [checking, setChecking] = useState(false);
  const [preview, setPreview] = useState(null);
  const [changeBusy, setChangeBusy] = useState(false);
  const [changeAttempted, setChangeAttempted] = useState(false);

  useEffect(() => {
    if (data?.location_limit) setLocations(data.location_limit);
  }, [data?.location_limit]);

  useEffect(() => {
    if (data?.pending_change) { setPreview(data.pending_change); setChangeAttempted(true); }
    else if (data && changeAttempted) setPreview(null);
  }, [data?.pending_change, data?.billing_status]);

  async function cancelCheckout() {
    setChecking(true);
    try {
      await api.post('/billing-account/checkout-cancel');
      await refetch();
      setConfirmation('Stripe confirmed the checkout was canceled. You may select different terms.');
    } catch (err) { setConfirmation(err?.response?.data?.error || 'Cancellation is not confirmed. Refresh billing status before trying again.'); }
    finally { setChecking(false); }
  }

  async function checkStatus() {
    setChecking(true);
    try {
      const { data: result } = await api.post('/billing-account/refresh');
      await refetch();
      setConfirmation(result.billing_status === 'active' ? 'Stripe confirmed your subscription is active.' : 'Activation is not yet confirmed. Check again shortly, or contact Cervica if payment was completed.');
    } catch (err) { setConfirmation(err?.response?.data?.error || 'We could not confirm billing status. Please try again.'); }
    finally { setChecking(false); }
  }

  // Returning from Stripe checkout: ?checkout=success|canceled
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('checkout');
    if (!result) return;
    if (result === 'success') {
      setConfirmation('Checking your subscription with Stripe…');
      checkStatus();
    } else if (result === 'canceled') {
      toast('Checkout was closed. You can resume the same checkout below.');
    }
    params.delete('checkout');
    const qs = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`);
  }, [refetch]);

  async function subscribe(planKey) {
    setBusyPlan(planKey);
    try {
      const changing = data.has_subscription && !['canceled', 'incomplete_expired'].includes(data.stripe_status);
      const { data: r } = await api.post(changing ? '/billing-account/change-preview' : '/billing-account/checkout', { plan: planKey, locations: planKey === 'multi_location' ? locations : 1 });
      if (changing) { setPreview(r); setChangeAttempted(false); setBusyPlan(null); }
      else window.location.assign(r.url);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not start checkout');
      setBusyPlan(null);
    }
  }

  async function confirmChange() {
    setChangeBusy(true);
    setChangeAttempted(true);
    try {
      const {data: result} = await api.post('/billing-account/change-confirm', {id: preview.id});
      await refetch();
      if (result.confirmed) {
        setPreview(null);
        setConfirmation('Stripe confirmed your updated subscription.');
      } else {
        setConfirmation('Your plan change is awaiting payment confirmation. Open Manage billing to resolve payment, then refresh billing status. Your existing plan remains in place.');
      }
    } catch (err) {
      setConfirmation(err?.response?.data?.error || 'The change is not confirmed. Retry the same change below; do not start another.');
    } finally { setChangeBusy(false); }
  }

  async function openPortal() {
    setPortalBusy(true);
    try {
      const { data: r } = await api.post('/billing-account/portal');
      window.location.assign(r.url);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not open billing portal');
      setPortalBusy(false);
    }
  }

  if (isLoading) return <Skeleton.Table rows={4} cols={3} />;
  if (!data) return null;

  const trialDaysLeft = data.trial_ends_at
    ? Math.ceil((new Date(data.trial_ends_at).getTime() - Date.now()) / 86400000)
    : null;
  const onTrial = data.plan === 'trial';

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h2 className="text-[16px] font-bold text-gray-900">Subscription</h2>
        <p className="text-[13px] text-gray-500">
          Your Cervica plan and billing. Card entry and invoices are handled on Stripe's secure pages; card details never touch Cervica.
        </p>
      </div>

      {confirmation && <div role="status" className="rounded-lg border border-teal-200 bg-teal-50 p-4 text-sm">{confirmation}</div>}
      {!data.is_legacy_plan && <Button variant="secondary" onClick={checkStatus} isLoading={checking} disabled={!!busyPlan || portalBusy || changeBusy}>Refresh billing status</Button>}

      {data.checkout_plan && <div className="rounded-lg border bg-white p-4 space-y-2">
        <p>Saved checkout: {data.checkout_plan.locations} location(s), ${data.checkout_plan.monthly}/month.</p>
        <Button disabled={checking || !!busyPlan} onClick={async () => {
          setBusyPlan(data.checkout_plan.planKey);
          try { const {data:r}=await api.post('/billing-account/checkout', {plan:data.checkout_plan.planKey,locations:data.checkout_plan.locations}); window.location.assign(r.url); }
          catch (err) { setConfirmation(err?.response?.data?.error || 'Could not recover checkout.'); setBusyPlan(null); }
        }}>Resume saved checkout</Button>
        <Button variant="secondary" disabled={checking || !!busyPlan} onClick={cancelCheckout}>Cancel saved checkout</Button>
      </div>}
      {preview && <section aria-label="Review subscription change" className="rounded-xl border border-teal-300 bg-white p-5 space-y-3">
        <h3 className="font-semibold">Review subscription change</h3>
        <p>{preview.plan} · {preview.locations} location{preview.locations === 1 ? '' : 's'} · ${preview.monthly}/month before tax</p>
        <p>Stripe estimates {(preview.amount_due / 100).toLocaleString('en-US', {style: 'currency', currency: preview.currency})} due now, including applicable prorations and credits. Your recurring bill changes when Stripe confirms the update.</p>
        <p className="text-sm text-gray-500">Changing tiers can change available features. Existing records are preserved.</p>
        <div className="flex gap-2">
          <Button onClick={confirmChange} isLoading={changeBusy}>{changeAttempted ? 'Recover this change' : 'Confirm plan and payment'}</Button>
          {!changeAttempted && <Button variant="secondary" onClick={() => setPreview(null)}>Keep current plan</Button>}
        </div>
      </section>}
      {/* Current state card */}
      <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-900/[0.04]">
        {data.is_legacy_plan ? (
          <div className="flex items-start gap-3">
            <BadgeCheck className="mt-0.5 h-5 w-5 text-emerald-600" />
            <div>
              <p className="text-[14px] font-semibold text-gray-900">Legacy plan ({data.plan})</p>
              <p className="text-[12px] text-gray-500 mt-0.5">
                This practice predates online subscriptions. Nothing to manage here.
              </p>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-[12px] font-semibold uppercase tracking-wide text-gray-500">Current plan</p>
              <p className="text-[18px] font-bold text-gray-900">
                {onTrial ? 'Free trial' : (data.plan_label || data.plan || 'None')}
              </p>
              {onTrial && trialDaysLeft != null && (
                <p className={`text-[12px] mt-0.5 ${trialDaysLeft <= 0 ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                  {trialDaysLeft > 0
                    ? `${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} remaining. Pick a plan below to activate.`
                    : 'Trial ended. Your account is read-only until you activate a plan below.'}
                </p>
              )}
              {data.location_limit && <p className="mt-1 text-xs text-gray-500">{data.active_locations} active of {data.location_limit} subscribed location(s)</p>}
              {data.billing_status === 'pending' && <p className="mt-1 text-sm text-amber-700">Payment confirmation is pending. Refresh billing status or open Manage billing before trying another payment.</p>}
              {data.billing_status === 'past_due' && (
                <p className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-0.5 text-[11px] font-bold text-amber-700">
                  <AlertTriangle className="h-3 w-3" /> Payment failed. Update your card in Manage billing.
                </p>
              )}
              {data.billing_status === 'canceled' && (
                <p className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-0.5 text-[11px] font-bold text-red-600">
                  Subscription ended. Resubscribe below to reactivate.
                </p>
              )}
            </div>
            {data.has_subscription && (
              <Button variant="secondary" onClick={openPortal} isLoading={portalBusy} disabled={!!busyPlan || changeBusy}>
                <CreditCard className="mr-1.5 h-4 w-4" /> Manage billing
              </Button>
            )}
          </div>
        )}
      </div>

      {!data.configured && !data.is_legacy_plan && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-[12px] text-amber-900">
          Online checkout is not live yet. Contact Cervica to activate your subscription.
        </div>
      )}

      {/* Plan grid */}
      {!data.is_legacy_plan && (
        <div className="grid gap-3 sm:grid-cols-2">
          {(data.plans || []).map((p) => {
            const isCurrent = !onTrial && data.plan === p.key && data.billing_status !== 'canceled' && (p.key !== 'multi_location' || locations === (data.location_limit || 2));
            return (
              <div key={p.key} className={`rounded-xl bg-white p-5 shadow-sm ring-1 ${isCurrent ? 'ring-2 ring-primary-500' : 'ring-gray-900/[0.04]'}`}>
                <div className="flex items-baseline justify-between">
                  <p className="text-[14px] font-bold text-gray-900">{p.label}</p>
                  <p className="text-[16px] font-bold text-gray-900">
                    ${p.monthly + (p.key === 'multi_location' ? (locations - 2) * 299 : 0)}<span className="text-[11px] font-medium text-gray-500">/mo</span>
                  </p>
                </div>
                <p className="mt-1 text-[12px] leading-relaxed text-gray-500">{p.blurb}</p>
                {p.key === 'multi_location' && <label className="mt-3 block text-sm">Locations in your organization
                  <select aria-label="Number of locations" value={locations} disabled={!!busyPlan || portalBusy || !!preview} onChange={e => setLocations(Number(e.target.value))} className="ml-2 rounded border p-2">
                    {[2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                  <span className="mt-1 block text-xs text-gray-500">Two included; $299/month each additional. Six or more: contact Cervica for pricing.</span>
                </label>}
                <div className="mt-3">
                  {isCurrent ? (
                    <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-primary-700">
                      <BadgeCheck className="h-4 w-4" /> Current plan
                    </span>
                  ) : (
                    <Button
                      size="sm"
                      disabled={!p.available || !!busyPlan || portalBusy || !!preview || changeBusy || data.active_locations > (p.key === 'multi_location' ? locations : 1)}
                      isLoading={busyPlan === p.key}
                      onClick={() => subscribe(p.key)}
                    >
                      {data.has_subscription && !['canceled', 'incomplete_expired'].includes(data.stripe_status) ? 'Review plan change' : 'Subscribe / resume checkout'}
                    </Button>
                  )}
                  {!p.available && (
                    <p className="mt-1 text-[10px] text-gray-400">Not available for online checkout yet</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!data.is_legacy_plan && (
        <p className="text-[11px] text-gray-400">
          Month to month, cancel anytime from Manage billing. Prices shown are monthly, before applicable tax. Single-location plans cover one office. Multi-Location covers the selected number of offices within one organization, not unlimited practices.
        </p>
      )}
    </div>
  );
}
